import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, DollarSign, CheckCircle, XCircle, Clock, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const safeFormat = (dateStr, fmt, opts) => {
  if (!dateStr) return '';
  try { const d = new Date(dateStr); if (isNaN(d.getTime())) return ''; return format(d, fmt, opts); } catch { return ''; }
};
import { useAuth } from '@/contexts/AuthContext';

export default function SalesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isPrivileged = user?.role === 'admin' || user?.role === 'supervisor';
  const [sales, setSales] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [operators, setOperators] = useState([]);
  const [discountDialog, setDiscountDialog] = useState(null);
  const [discountAmount, setDiscountAmount] = useState('');
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectObservation, setRejectObservation] = useState('');
  const [editDialog, setEditDialog] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editClosedBy, setEditClosedBy] = useState('');
  // Filtro de período
  const [datePreset, setDatePreset] = useState('all'); // all | today | week | month | custom
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Calcula datas a partir do preset
  const computeRange = (preset) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === 'today') {
      return { from: iso(now), to: iso(now) };
    }
    if (preset === 'week') {
      const start = new Date(now);
      start.setDate(now.getDate() - 6);
      return { from: iso(start), to: iso(now) };
    }
    if (preset === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: iso(start), to: iso(now) };
    }
    return { from: '', to: '' };
  };

  useEffect(() => {
    fetchSales();
    if (isPrivileged) fetchOperators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged, datePreset, dateFrom, dateTo]);

  const fetchOperators = async () => {
    try {
      const res = await api.getOperators();
      setOperators(res.data || []);
    } catch {
      // silent
    }
  };

  const fetchSales = async () => {
    try {
      // Determina o range a enviar
      let params = {};
      if (datePreset !== 'all' && datePreset !== 'custom') {
        const r = computeRange(datePreset);
        params.date_from = r.from;
        params.date_to = r.to;
      } else if (datePreset === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const response = await api.getSales(params);
      setSales(response.data);
    } catch { toast.error('Erro ao carregar vendas'); }
  };

  const handleApplyDiscount = async () => {
    if (!discountDialog || !discountAmount) return;
    try {
      await api.applyDiscount(discountDialog.id, parseFloat(discountAmount));
      toast.success('Venda aprovada com desconto!');
      setDiscountDialog(null);
      setDiscountAmount('');
      fetchSales();
    } catch { toast.error('Erro ao aplicar desconto'); }
  };

  const handleApprove = async (saleId) => {
    try {
      await api.approveSale(saleId);
      toast.success('Venda aprovada!');
      fetchSales();
    } catch { toast.error('Erro ao aprovar venda'); }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    try {
      await api.rejectSale(rejectDialog.id, rejectObservation);
      toast.success('Venda reprovada.');
      setRejectDialog(null);
      setRejectObservation('');
      fetchSales();
    } catch { toast.error('Erro ao reprovar venda'); }
  };

  const openEditDialog = (sale) => {
    setEditDialog(sale);
    setEditAmount(String(sale.amount || ''));
    setEditClosedBy(sale.closed_by || '');
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    try {
      const payload = {};
      const newAmount = parseFloat(editAmount);
      if (!isNaN(newAmount) && newAmount !== editDialog.amount) payload.amount = newAmount;
      if (editClosedBy && editClosedBy !== editDialog.closed_by) payload.closed_by = editClosedBy;
      if (Object.keys(payload).length === 0) {
        toast.info('Nenhuma alteracao para salvar');
        setEditDialog(null);
        return;
      }
      await api.editSale(editDialog.id, payload);
      toast.success('Venda atualizada com sucesso');
      setEditDialog(null);
      fetchSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao editar venda');
    }
  };

  const handleDelete = async (sale) => {
    if (!window.confirm(`Excluir venda de ${sale.lead_name || ''} - R$ ${(sale.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}?\n\nSe for a unica venda do lead, ele volta para 'Sem Contato'.`)) return;
    try {
      await api.deleteSale(sale.id);
      toast.success('Venda excluida com sucesso');
      fetchSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao excluir venda');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Aprovada</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Reprovada</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Em Analise</Badge>;
    }
  };

  const totalRevenue = sales.filter(s => s.status === 'approved').reduce((sum, sale) => sum + sale.amount, 0);
  const pendingCount = sales.filter(s => s.status === 'pending').length;
  const approvedCount = sales.filter(s => s.status === 'approved').length;
  const rejectedCount = sales.filter(s => s.status === 'rejected').length;

  // Filtro de busca por nome/telefone/produto + status
  const q = searchQuery.trim().toLowerCase();
  const filteredSales = sales.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (!q) return true;
    const phone = (s.lead_phone || s.phone || '').toLowerCase();
    const name = (s.lead_name || '').toLowerCase();
    const product = (s.product_name || s.description || '').toLowerCase();
    const closedBy = (s.closed_by_name || '').toLowerCase();
    return name.includes(q) || phone.includes(q) || product.includes(q) || closedBy.includes(q);
  });

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vendas
            </h1>
            <p className="text-slate-600">Acompanhe todas as vendas registradas</p>
          </div>

          <Card className="p-6 mb-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-700">Receita Aprovada</p>
                <h3 className="text-4xl font-bold text-emerald-900 mt-2">
                  R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </h3>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-amber-700">{pendingCount} em analise</span>
                  <span className="text-emerald-700">{approvedCount} aprovadas</span>
                  <span className="text-red-700">{rejectedCount} reprovadas</span>
                </div>
              </div>
              <TrendingUp className="w-16 h-16 text-emerald-600" />
            </div>
          </Card>

          <Card>
            <div className="p-4 border-b flex flex-wrap gap-3 items-center">
              <Input
                type="search"
                placeholder="Buscar por nome, telefone ou produto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
                data-testid="sales-search-input"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded px-3 py-1.5 text-sm"
                data-testid="sales-status-filter"
              >
                <option value="all">Todos status</option>
                <option value="pending">Em análise</option>
                <option value="approved">Aprovadas</option>
                <option value="rejected">Reprovadas</option>
              </select>
              <select
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
                className="border rounded px-3 py-1.5 text-sm"
                data-testid="sales-date-preset"
              >
                <option value="all">Todo período</option>
                <option value="today">Hoje</option>
                <option value="week">Últimos 7 dias</option>
                <option value="month">Este mês</option>
                <option value="custom">Personalizado</option>
              </select>
              {datePreset === 'custom' && (
                <>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="max-w-[150px]"
                    data-testid="sales-date-from"
                  />
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="max-w-[150px]"
                    data-testid="sales-date-to"
                  />
                </>
              )}
              <span className="ml-auto text-sm text-slate-500">
                {filteredSales.length} de {sales.length} vendas
              </span>
            </div>
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead>Valor Original</TableHead>
                  <TableHead>Valor Final</TableHead>
                  <TableHead>Vendedor</TableHead>
                  <TableHead>Status</TableHead>
                  {isPrivileged && <TableHead>Acoes</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSales.map((sale) => (
                  <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`}>
                    <TableCell className="text-sm">
                      {safeFormat(sale.created_at, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="font-medium">{sale.lead_name || '-'}</TableCell>
                    <TableCell>{sale.product_name || sale.description || '-'}</TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm">{
                          {credit_card:'Credito',debit_card:'Debito',pix:'PIX',boleto:'Boleto',cash:'Dinheiro',transfer:'Transf.'}[sale.payment_method] || sale.payment_method || '-'
                        }</span>
                        {sale.installments > 1 && <span className="text-xs text-slate-500 ml-1">({sale.installments}x)</span>}
                        {isPrivileged && sale.card_data && !sale.card_data.hidden && (
                          <p className="text-xs text-slate-400 mt-1">
                            Cartao: ****{sale.card_data.number_last4} - {sale.card_data.holder}
                          </p>
                        )}
                        {!isPrivileged && sale.card_data && (
                          <p className="text-xs text-slate-400 mt-1">Cartao: ****{sale.card_data.number_last4}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      R$ {(sale.original_amount || sale.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-emerald-600 font-semibold">
                      R$ {(sale.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>{sale.closed_by_name || '-'}</TableCell>
                    <TableCell>
                      {getStatusBadge(sale.status)}
                      {sale.review_observation && (
                        <p className="text-xs text-slate-500 mt-1 italic">{sale.review_observation}</p>
                      )}
                    </TableCell>
                    {isPrivileged && (
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {sale.status === 'pending' && (
                            <>
                              <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" onClick={() => handleApprove(sale.id)} data-testid={`approve-sale-${sale.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Aprovar
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setRejectDialog(sale)} data-testid={`reject-sale-${sale.id}`}>
                                <XCircle className="w-3 h-3 mr-1" /> Reprovar
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => { setDiscountDialog(sale); setDiscountAmount(String(sale.amount)); }} data-testid={`discount-sale-${sale.id}`}>
                                <DollarSign className="w-3 h-3 mr-1" /> Desconto
                              </Button>
                            </>
                          )}
                          {!sale.id?.startsWith('lead-') && (
                            <>
                              <Button variant="outline" size="sm" className="text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => openEditDialog(sale)} data-testid={`edit-sale-${sale.id}`}>
                                <Pencil className="w-3 h-3 mr-1" /> Editar
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDelete(sale)} data-testid={`delete-sale-${sale.id}`}>
                                <Trash2 className="w-3 h-3 mr-1" /> Excluir
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Discount Dialog */}
          <Dialog open={!!discountDialog} onOpenChange={(open) => { if (!open) setDiscountDialog(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Aprovar com Desconto</DialogTitle>
              </DialogHeader>
              {discountDialog && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Produto: <strong>{discountDialog.product_name}</strong><br />
                    Valor original: R$ {(discountDialog.original_amount || discountDialog.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br />
                    {discountDialog.observation && <>Observacao do vendedor: <em>{discountDialog.observation}</em></>}
                  </p>
                  <div>
                    <Label>Novo valor (R$)</Label>
                    <Input type="number" step="0.01" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} data-testid="discount-amount-input" />
                  </div>
                  <Button onClick={handleApplyDiscount} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="apply-discount-button">
                    Aprovar com Desconto
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Reject Dialog */}
          <Dialog open={!!rejectDialog} onOpenChange={(open) => { if (!open) setRejectDialog(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reprovar Venda</DialogTitle>
              </DialogHeader>
              {rejectDialog && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Produto: <strong>{rejectDialog.product_name}</strong><br />
                    Valor: R$ {(rejectDialog.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br />
                    Vendedor: {rejectDialog.closed_by_name}
                  </p>
                  <div>
                    <Label>Motivo da reprovacao (opcional)</Label>
                    <Input value={rejectObservation} onChange={(e) => setRejectObservation(e.target.value)} placeholder="Ex: Dados incompletos, valor incorreto..." data-testid="reject-observation-input" />
                  </div>
                  <Button onClick={handleReject} className="w-full bg-red-600 hover:bg-red-700" data-testid="confirm-reject-button">
                    Confirmar Reprovacao
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={!!editDialog} onOpenChange={(open) => { if (!open) setEditDialog(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Venda</DialogTitle>
              </DialogHeader>
              {editDialog && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Lead: <strong>{editDialog.lead_name}</strong><br />
                    Produto: {editDialog.product_name}
                  </p>
                  <div>
                    <Label>Valor da venda (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      data-testid="edit-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Vendedor</Label>
                    <select
                      value={editClosedBy}
                      onChange={(e) => setEditClosedBy(e.target.value)}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm"
                      data-testid="edit-closedby-select"
                    >
                      <option value="">— Mantém atual —</option>
                      {operators.map((op) => (
                        <option key={op.id} value={op.id}>{op.name} ({op.email})</option>
                      ))}
                    </select>
                  </div>
                  <Button onClick={handleEditSave} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-edit-button">
                    Salvar Alteracoes
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
}
